import { Component, computed, input, output } from '@angular/core';

/**
 * Eye / eye-off control for password or other secret inputs.
 * Parent should wrap the input in a positioned container (e.g. `.password-input-wrap`).
 */
@Component({
  selector: 'app-password-visibility-toggle',
  standalone: true,
  template: `
    <button
      type="button"
      class="app-pw-toggle"
      [disabled]="disabled()"
      (click)="toggled.emit()"
      [attr.aria-pressed]="visible()"
      [attr.aria-label]="ariaLabelText()"
    >
      @if (visible()) {
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M3 3l18 18M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      } @else {
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      }
    </button>
  `,
  styles: [
    `
      .app-pw-toggle {
        position: absolute;
        right: 0.4rem;
        top: 50%;
        transform: translateY(-50%);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        padding: 0;
        margin: 0;
        border: none;
        border-radius: var(--ds-radius-md, 10px);
        background: transparent;
        color: var(--ds-color-text-muted, #8a96a3);
        cursor: pointer;
        transition:
          color 0.15s ease,
          background 0.15s ease;
      }

      .app-pw-toggle:hover:not(:disabled) {
        color: var(--ds-color-text-secondary, #5b6773);
        background: rgba(15, 20, 24, 0.06);
      }

      .app-pw-toggle:focus-visible {
        outline: 2px solid var(--ds-color-brand-accent-shell, #0f3d2e);
        outline-offset: 2px;
      }

      .app-pw-toggle:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
    `,
  ],
})
export class PasswordVisibilityToggleComponent {
  /** When true, the secret value is shown (plain text). */
  visible = input(false);
  disabled = input(false);
  labelReveal = input('Show password');
  labelHide = input('Hide password');

  toggled = output<void>();

  readonly ariaLabelText = computed(() =>
    this.visible() ? this.labelHide() : this.labelReveal(),
  );
}
